import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as ThaiIcon } from "../assets/icons/layout1/thai.svg";
import { ReactComponent as EngIcon } from "../assets/icons/layout1/eng.svg";


/**
 * LanguageSwitcher — dropdown เปลี่ยนภาษา ไทย / English
 * Props:
 *   variant: "light" (default) | "dark" | "square"
 */
export default function LanguageSwitcher({ variant = "light" }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const currentLang = i18n.language === "en" ? "en" : "th";

  const languages = [
    {
      code: "th",
      label: "ภาษาไทย",
      flag: <ThaiIcon className="w-full h-full object-cover" />,
    },
    {
      code: "en",
      label: "English",
      flag: <EngIcon className="w-full h-full object-cover" />,
    },
  ];

  const current = languages.find((l) => l.code === currentLang) || languages[0];

  // ปิด dropdown เมื่อคลิกข้างนอก
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectLang = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const isSquare = variant === "square";
  const isLight = variant === "light" || isSquare;

  const renderFlag = (flag) => (
    <div className="w-6 h-6 shrink-0 rounded-full overflow-hidden flex items-center justify-center bg-slate-100 border border-slate-100">
      {flag}
    </div>
  );

  const buttonClass = isSquare
    ? "flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white aspect-square shadow-md hover:bg-slate-50 active:scale-95 transition-all shrink-0"
    : [
        "flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-sm font-extrabold transition-all",
        "hover:shadow-md active:scale-95",
        isLight
          ? "border-slate-200 bg-white text-blue-800 hover:bg-slate-50"
          : "border-white/20 bg-white/10 text-white hover:bg-white/20",
      ].join(" ");

  return (
    <div ref={ref} className="relative select-none flex items-stretch">
      {/* Trigger Button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="เปลี่ยนภาษา / Change language"
        className={buttonClass}
      >
        {renderFlag(current.flag)}
        {isSquare ? (
          <svg className="w-1 h-1 text-slate-600 shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M0 7.33l12 12 12-12z" />
          </svg>
        ) : (
          <>
            <span className="hidden sm:block text-xs">
              {t(`common.lang.${currentLang}`)}
            </span>
            <svg
              className={`w-3 h-3 transition-transform duration-200 ${open ? "rotate-180" : ""} ${
                isLight ? "text-slate-400" : "text-white/70"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {/* Dropdown Menu */}
      {open && (
        <div
          className={[
            "absolute right-0 top-full mt-2 z-[9999]",
            "min-w-[160px] rounded-xl border shadow-xl overflow-hidden",
            "animate-[fadeSlideDown_0.15s_ease-out]",
            isLight ? "bg-white border-slate-200" : "bg-slate-800 border-white/10",
          ].join(" ")}
        >
          {languages.map((lang) => {
            const isActive = lang.code === currentLang;
            return (
              <button
                key={lang.code}
                onClick={() => selectLang(lang.code)}
                className={[
                  "w-full flex items-center gap-3 px-4 py-3 text-sm font-bold transition-colors",
                  isActive
                    ? isLight ? "bg-blue-50 text-blue-700" : "bg-blue-600/30 text-white"
                    : isLight ? "text-slate-700 hover:bg-slate-50" : "text-slate-200 hover:bg-white/10",
                ].join(" ")}
              >
                {renderFlag(lang.flag)}
                <span className="flex-1 text-left">{lang.label}</span>
                {isActive && (
                  <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-4.121-4.121a1 1 0 011.414-1.414L8.414 12.172l7.879-7.879a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
